import React, { useState } from "react";
import { X, Link2, Trash2, ExternalLink } from "lucide-react";

import gDocIcon from "../../assets/gDoc.png";
import gSheetIcon from "../../assets/gSheet.png";
import dropboxIcon from "../../assets/dropbox.png";
import gSlideIcon from "../../assets/gSlide.png";
import gFormIcon from "../../assets/gForm.png";
import wordIcon from "../../assets/word.png";
import excelIcon from "../../assets/excel.png";
import powerpointIcon from "../../assets/powerpoint.png";
import boxIcon from "../../assets/box.png";
import onedriveIcon from "../../assets/onedrive.png";
import notionIcon from "../../assets/notion.png";
import figmaIcon from "../../assets/figma.png";
import airtableIcon from "../../assets/airtable.png";
import trelloIcon from "../../assets/trello.png";
import canvaIcon from "../../assets/canva.png";

const getMimeType = (url) => {
	// Google Workspace
	if (url.includes("docs.google.com/document"))
		return "application/vnd.google-apps.document";
	if (url.includes("docs.google.com/spreadsheets"))
		return "application/vnd.google-apps.spreadsheet";
	if (url.includes("docs.google.com/presentation"))
		return "application/vnd.google-apps.presentation";
	if (url.includes("docs.google.com/forms"))
		return "application/vnd.google-apps.form";

	// Get extension from URL
	const extension = url.split(".").pop().toLowerCase();

	// Common MIME types
	const mimeTypes = {
		doc: "application/msword",
		docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		xls: "application/vnd.ms-excel",
		xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		ppt: "application/vnd.ms-powerpoint",
		pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
		pdf: "application/pdf",
		txt: "text/plain",
	};

	return mimeTypes[extension] || "application/octet-stream";
};

const getDocumentIcon = (url) => {
	// Google Workspace
	if (url.includes("docs.google.com/document")) {
		return {
			src: gDocIcon,
			alt: "Google Doc",
		};
	} else if (url.includes("docs.google.com/spreadsheets")) {
		return {
			src: gSheetIcon,
			alt: "Google Sheet",
		};
	} else if (url.includes("docs.google.com/presentation")) {
		return {
			src: gSlideIcon,
			alt: "Google Slides",
		};
	} else if (url.includes("docs.google.com/forms")) {
		return {
			src: gFormIcon,
			alt: "Google Form",
		};
	}

	// Microsoft Office Online
	else if (url.includes("office.live.com/word")) {
		return {
			src: wordIcon,
			alt: "Word Online",
		};
	} else if (url.includes("office.live.com/excel")) {
		return {
			src: excelIcon,
			alt: "Excel Online",
		};
	} else if (url.includes("office.live.com/powerpoint")) {
		return {
			src: powerpointIcon,
			alt: "PowerPoint Online",
		};
	}

	// Cloud Storage
	else if (url.includes("dropbox.com")) {
		return {
			src: dropboxIcon,
			alt: "Dropbox",
		};
	} else if (url.includes("box.com")) {
		return {
			src: boxIcon,
			alt: "Box",
		};
	} else if (url.includes("onedrive.live.com")) {
		return {
			src: onedriveIcon,
			alt: "OneDrive",
		};
	}

	// Collaboration Tools
	else if (url.includes("notion.so")) {
		return {
			src: notionIcon,
			alt: "Notion",
		};
	} else if (url.includes("figma.com")) {
		return {
			src: figmaIcon,
			alt: "Figma",
		};
	} else if (url.includes("airtable.com")) {
		return {
			src: airtableIcon,
			alt: "Airtable",
		};
	} else if (url.includes("trello.com")) {
		return {
			src: trelloIcon,
			alt: "Trello",
		};
	} else if (url.includes("canva.com")) {
		return {
			src: canvaIcon,
			alt: "Canva",
		};
	}

	// Default to null for generic link icon
	return null;
};

const DocumentsModal = ({ documents = [], onChange, onClose }) => {
	const [url, setUrl] = useState("");
	const [title, setTitle] = useState("");
	const [error, setError] = useState("");

	const validateAndAddLink = () => {
		try {
			new URL(url);
		} catch {
			setError("Please enter a valid URL");
			return;
		}

		const newDocument = {
			type: "link",
			url: url,
			title: title || url,
			id: crypto.randomUUID(),
			mimeType: getMimeType(url),
		};

		onChange([...documents, newDocument]);
		setUrl("");
		setTitle("");
		setError("");
	};

	const handleRemoveDocument = (documentId) => {
		onChange(documents.filter((doc) => doc.id !== documentId));
	};

	return (
		<>
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={onClose}
			/>

			<div className="fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl flex flex-col h-[55vh]">
				{/* Sticky Header */}
				<div className="sticky top-0 z-20 bg-white dark:bg-gray-900 rounded-t-xl">
					<div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							Add document links
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
						>
							<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
						</button>
					</div>

					{/* Input Section */}
					<div className="p-4 border-b border-gray-100 dark:border-gray-800">
						<div className="space-y-4">
							<div className="space-y-2">
								<input
									type="url"
									value={url}
									onChange={(e) => {
										setUrl(e.target.value);
										setError("");
									}}
									placeholder="Paste document link (Google Drive, Dropbox, etc.)"
									className={`w-full px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg 
                    text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400
                    focus:outline-none focus:ring-2 
                    ${
											error
												? "ring-2 ring-red-500 focus:ring-red-500"
												: "focus:ring-blue-500"
										}`}
								/>
								<input
									type="text"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									placeholder="Document title (optional)"
									className="w-full px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg 
                    text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							{error && (
								<p className="text-sm text-red-500 dark:text-red-400">
									{error}
								</p>
							)}
							<div className="flex justify-end">
								<button
									type="button"
									onClick={validateAndAddLink}
									disabled={!url}
									className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors
                    ${
											url
												? "text-white bg-blue-600 hover:bg-blue-700"
												: "text-gray-400 bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
										}`}
								>
									Add link
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Scrollable Content */}
				<div className="flex-1 overflow-y-auto min-h-0 p-4 scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
					{documents.length > 0 && (
						<div className="space-y-2">
							<div className="text-sm font-medium text-gray-700 dark:text-gray-300">
								Attached documents ({documents.length})
							</div>
							<div className="space-y-1">
								{documents.map((doc) => {
									const iconInfo = getDocumentIcon(doc.url);
									return (
										<div
											key={doc.id}
											className="flex items-center justify-between px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg group"
										>
											<div className="flex items-center gap-2 min-w-0">
												{iconInfo ? (
													<img
														src={iconInfo.src}
														alt={iconInfo.alt}
														className="w-4 h-4 shrink-0 object-contain"
													/>
												) : (
													<Link2 className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
												)}
												<span className="text-sm text-gray-900 dark:text-gray-100 truncate">
													{doc.title}
												</span>
											</div>
											<div className="flex items-center gap-2">
												<a
													href={doc.url}
													target="_blank"
													rel="noopener noreferrer"
													className="p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity
                              hover:bg-gray-200 dark:hover:bg-gray-700
                              text-gray-500 dark:text-gray-400
                              hover:text-blue-600 dark:hover:text-blue-400"
												>
													<ExternalLink className="w-4 h-4" />
												</a>
												<button
													type="button"
													onClick={() => handleRemoveDocument(doc.id)}
													className="p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity
                              hover:bg-gray-200 dark:hover:bg-gray-700
                              text-gray-500 dark:text-gray-400
                              hover:text-red-600 dark:hover:text-red-400"
												>
													<Trash2 className="w-4 h-4" />
												</button>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>

				{/* Sticky Footer */}
				<div className="border-t border-gray-100 dark:border-gray-800">
					<div className="p-4">
						<button
							type="button"
							onClick={onClose}
							className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
						>
							Done
						</button>
					</div>
				</div>
			</div>
		</>
	);
};

export default DocumentsModal;
